import { useContext, useEffect, useRef, useState } from 'react';
import AuthContext from '../../../providers/AuthContext';
import { createTeacherNote, updateTeacherNote, deleteNote } from './notesApi';
import '../../../css/workspace-notes.css';

// Port of english-app's NotePin.tsx (TS -> JS) - same "pinezka + rozkładany
// listek" metaphor, same geometric pointermove-based close (NOT
// onMouseLeave - React's synthetic mouseleave can permanently stop firing
// when the DOM node under the cursor gets swapped mid-render, which is
// exactly what happens here when the composer button flips to a form in
// the same spot). Extends the original with a TEACHER compose/edit/delete
// path - the student platform only ever creates authorType=STUDENT notes,
// this is the one place authorType=TEACHER gets written.

const NOTE_COLORS = ['#ffe066', '#ffb3c6', '#b9fbc0', '#a0c4ff', '#d0bfff', '#ffd6a5'];
const MAX_VISIBLE_PAPERS = 3;
const PAPER_TRANSFORMS = [
   { rotate: -12, x: -6, y: 1 },
   { rotate: 10, x: 7, y: 3 },
   { rotate: -4, x: 0, y: 6 },
];
const CORNER_CUT_CLASSES = ['note-pin-paper-cut-tr', 'note-pin-paper-cut-tl', 'note-pin-paper-cut-br', 'note-pin-paper-cut-bl'];
const CLOSE_MARGIN_PX = 16;

function safeColor(config) {
   if (!config) return null;
   try {
      const parsed = JSON.parse(config);
      return typeof parsed.color === 'string' ? parsed.color : null;
   } catch (e) {
      return null;
   }
}

function formatNoteDate(dAdd) {
   if (!dAdd) return '';
   const date = new Date(dAdd);
   if (Number.isNaN(date.getTime())) return '';
   return date.toLocaleDateString('uk-UA', { day: 'numeric', month: 'short' });
}

function teacherLabel(note) {
   const t = note.teacherAuthor;
   if (!t) return 'Вчитель';
   const first = t.imie || t.firstName || t.name || '';
   const last = t.nazwisko || t.lastName || t.surname || '';
   const full = (first + ' ' + last).trim();
   return full || 'Вчитель';
}

// `note.studentAuthor` (explicit FK, mirrors teacherAuthor) is the real
// author - `note.workspace.student` is only a fallback for notes that
// predate this field, since in a group course a mirrored copy's `workspace`
// belongs to whichever member's copy this is, NOT necessarily who wrote it.
function studentLabel(note) {
   const s = note.studentAuthor || (note.workspace && note.workspace.student);
   if (!s) return 'Студент';
   const full = ((s.name || '') + ' ' + (s.surname || '')).trim();
   return full || 'Студент';
}

// `notes` is already filtered to just this element (parent groups the one
// bulk workspace fetch by target) - kept dumb/reusable rather than having
// this component do its own lookup, per this module's own data-hook doing
// that grouping once up front.
export default function NotePin({ notes, kind, targetId, workspaceId, onNotesChanged }) {
   const { userId } = useContext(AuthContext) || {};
   const [isOpen, setIsOpen] = useState(false);
   const [isComposing, setIsComposing] = useState(false);
   const [draft, setDraft] = useState('');
   const [color, setColor] = useState(NOTE_COLORS[0]);
   const [saving, setSaving] = useState(false);
   const [editingId, setEditingId] = useState(null);
   const [editDraft, setEditDraft] = useState('');
   const anchorRef = useRef(null);

   useEffect(() => {
      if (!isOpen || isComposing) return;
      function handlePointerMove(e) {
         const root = anchorRef.current;
         if (!root) return;
         const btn = root.querySelector('.note-pin-stack-btn');
         const leaf = root.querySelector('.note-pin-leaf');
         const rects = [btn && btn.getBoundingClientRect(), leaf && leaf.getBoundingClientRect()].filter(Boolean);
         if (rects.length === 0) return;
         const union = {
            left: Math.min(...rects.map(r => r.left)),
            right: Math.max(...rects.map(r => r.right)),
            top: Math.min(...rects.map(r => r.top)),
            bottom: Math.max(...rects.map(r => r.bottom)),
         };
         const inside = e.clientX >= union.left - CLOSE_MARGIN_PX && e.clientX <= union.right + CLOSE_MARGIN_PX
            && e.clientY >= union.top - CLOSE_MARGIN_PX && e.clientY <= union.bottom + CLOSE_MARGIN_PX;
         if (!inside) setIsOpen(false);
      }
      window.addEventListener('pointermove', handlePointerMove);
      return () => window.removeEventListener('pointermove', handlePointerMove);
   }, [isOpen, isComposing]);

   const own = notes || [];
   const hasNotes = own.length > 0;

   const openComposer = () => { setIsComposing(true); setIsOpen(true); };

   const handleSave = () => {
      const text = draft.trim();
      if (!text || saving) return;
      setSaving(true);
      createTeacherNote(workspaceId, kind, targetId, userId, text, color)
         .then(() => { setDraft(''); setIsComposing(false); onNotesChanged(); })
         .catch(() => {})
         .finally(() => setSaving(false));
   };

   const handleCancelComposer = () => {
      setIsComposing(false);
      setDraft('');
      if (!hasNotes) setIsOpen(false);
   };

   const startEdit = (note) => { setEditingId(note.id); setEditDraft(note.text); };
   const cancelEdit = () => { setEditingId(null); setEditDraft(''); };
   const saveEdit = (note) => {
      const text = editDraft.trim();
      if (!text || saving) return;
      setSaving(true);
      updateTeacherNote(note, text, safeColor(note.config) || NOTE_COLORS[0])
         .then(() => { setEditingId(null); onNotesChanged(); })
         .catch(() => {})
         .finally(() => setSaving(false));
   };
   const handleDelete = (note) => {
      if (!window.confirm('Видалити нотатку?')) return;
      deleteNote(note.id).then(() => onNotesChanged()).catch(() => {});
   };

   return (
      <div ref={anchorRef}
         className={'note-pin-anchor' + (hasNotes ? ' has-notes' : '') + (isOpen ? ' open' : '')}
         onMouseEnter={() => setIsOpen(true)}>
         <button type='button' className='note-pin-stack-btn'
            onClick={() => (hasNotes ? setIsOpen(v => !v) : openComposer())}
            aria-label={hasNotes ? 'Переглянути нотатки (' + own.length + ')' : 'Додати нотатку'}>
            <span className='note-pin-head' />
            {hasNotes ? (
               own.slice(-MAX_VISIBLE_PAPERS).map((note, i) => {
                  const t = PAPER_TRANSFORMS[i % PAPER_TRANSFORMS.length];
                  const cutClass = CORNER_CUT_CLASSES[note.id % CORNER_CUT_CLASSES.length];
                  return (
                     <span key={note.id} className={'note-pin-paper ' + cutClass}
                        style={{
                           '--paper-color': safeColor(note.config) || NOTE_COLORS[0],
                           transform: 'translate(' + t.x + 'px, ' + t.y + 'px) rotate(' + t.rotate + 'deg)',
                           zIndex: i + 1,
                        }} />
                  );
               })
            ) : (
               <span className='note-pin-ghost-paper'>+</span>
            )}
            {own.length > MAX_VISIBLE_PAPERS ? <span className='note-pin-count'>{own.length}</span> : ''}
         </button>

         {isOpen ? (
            <div className='note-pin-leaf'>
               {own.length > 0 ? (
                  <div className='note-pin-list'>
                     {own.map(note => (
                        <div key={note.id} className='note-pin-card' style={{ '--note-card-color': safeColor(note.config) || NOTE_COLORS[0] }}>
                           {editingId === note.id ? (
                              <>
                                 <textarea className='note-pin-composer-text' autoFocus value={editDraft}
                                    onChange={e => setEditDraft(e.target.value)} />
                                 <div className='note-pin-composer-actions'>
                                    <button type='button' className='note-pin-composer-cancel' onClick={cancelEdit}>Скасувати</button>
                                    <button type='button' className='note-pin-composer-save' disabled={!editDraft.trim() || saving}
                                       onClick={() => saveEdit(note)}>Зберегти</button>
                                 </div>
                              </>
                           ) : (
                              <>
                                 <p className='note-pin-card-text'>{note.text}</p>
                                 <div className='note-pin-card-footer'>
                                    <span className='note-pin-card-author'>
                                       {note.authorType === 'TEACHER' ? teacherLabel(note) : studentLabel(note)}
                                    </span>
                                    <span className='note-pin-card-date'>{formatNoteDate(note.dAdd)}</span>
                                 </div>
                                 {note.authorType === 'TEACHER' ? (
                                    <div className='note-pin-card-teacher-actions'>
                                       <span onClick={() => startEdit(note)}>Редагувати</span>
                                       <span onClick={() => handleDelete(note)}>Видалити</span>
                                    </div>
                                 ) : ''}
                              </>
                           )}
                        </div>
                     ))}
                  </div>
               ) : ''}

               {isComposing ? (
                  <div className='note-pin-composer' style={{ '--composer-color': color }}>
                     <span className='note-pin-composer-pin' />
                     <textarea autoFocus className='note-pin-composer-text' placeholder='Напишіть щось важливе...'
                        value={draft} onChange={e => setDraft(e.target.value)} />
                     <div className='note-pin-composer-colors'>
                        {NOTE_COLORS.map(c => (
                           <button key={c} type='button' className={'note-pin-color-swatch' + (c === color ? ' selected' : '')}
                              style={{ '--swatch-color': c }} onClick={() => setColor(c)} aria-label={'Колір нотатки ' + c} />
                        ))}
                     </div>
                     <div className='note-pin-composer-actions'>
                        <button type='button' className='note-pin-composer-cancel' onClick={handleCancelComposer}>Скасувати</button>
                        <button type='button' className='note-pin-composer-save' onClick={handleSave} disabled={!draft.trim() || saving}>
                           📌 Приколоти
                        </button>
                     </div>
                  </div>
               ) : (
                  <button type='button' className='note-pin-add-btn' onClick={openComposer}>+ Додати нотатку</button>
               )}
            </div>
         ) : ''}
      </div>
   );
}
